import { Fragment, useEffect, useState } from 'react';
import { GridCellParams } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { v4 } from 'uuid';
import { BehaviorSubject } from 'rxjs';
import { RpmAccount } from '../../../store/RpmAccount';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer } from '../../../store/Drawer';
import { GridCustomColDef } from '../../types';
import { formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { isData, isObserver } from '../../../store/User';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';

const handleAcctClick = (row: RpmAccount) => {
  addDrawer({
    id: v4(),
    data: row,
    ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: row.accountId })
  });
};

export const allCols: GridCustomColDef[] = [
  {
    headerName: 'Number',
    field: 'accountNumber',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <button
        className="flex items-center"
        onClick={() => handleAcctClick(params.row)}>
        <Icon
          className="p-3 mr-1"
          type="rpmAccountMed"
        />
        <Text
          size="sm14"
          color="indigo">
          {params?.value as string}
        </Text>
      </button>
    )
  },
  {
    headerName: 'First Run',
    field: 'firstCommissionRun',
    type: 'singleSelect',
    filterable: false,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    flex: 1
  },
  {
    headerName: 'Last Run',
    field: 'lastCommissionRun',
    type: 'singleSelect',
    filterable: false,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    flex: 1
  },
  {
    type: 'singleSelect',
    field: 'lastMrc',
    headerName: 'Last MRC',
    filterable: false,
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    flex: 1
  },
  {
    type: 'singleSelect',
    field: 'lastMrr',
    headerName: 'Last MRR',
    filterable: false,
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    flex: 1
  },
  {
    headerName: 'Link Count',
    width: 80,
    field: 'linkCount',
    type: 'singleSelect',
    filterable: false,
    description: 'Total number of Assets and PAs'
  }
];

export interface RPMMiniTableParams {
  id: string;
  fetchFunction: (
    id: string,
    drawer?: boolean
  ) => (filters?: ApiAdvancedFilter) => Promise<PaginatedResponse<RpmAccount> | undefined | void>;
  dataSubject: () => BehaviorSubject<PaginatedResponse<RpmAccount>>;
  loadingSubject: () => BehaviorSubject<boolean>;
  removeFunction?: (id: string) => Promise<void>;
  showRemove?: boolean;
  type?: string;
  drawer?: boolean;
}

export const RPMMiniTable = ({
  id,
  fetchFunction,
  dataSubject,
  loadingSubject,
  removeFunction,
  showRemove,
  type,
  drawer = false
}: RPMMiniTableParams) => {
  const [rpmData, setRpmData] = useState<PaginatedResponse<RpmAccount>>({} as PaginatedResponse<RpmAccount>);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const { setSnack } = useSnackbar();

  const handleRemove = async (row: RpmAccount) => {
    if (removeFunction && showRemove && row.accountId) {
      await removeFunction(row.linkId || row.accountId);
      setSnack({ message: `${type || 'Link'} removed`, type: 'success', open: true });
      fetchFunction(id, drawer)();
    }
  };
  const tableCols: GridCustomColDef[] = [...allCols];
  if (!isObserver() && !isData())
    tableCols.push({
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      type: 'singleSelect',
      valueOptions: [],
      sortable: false,
      renderCell: (params) => (
        <Fragment>
          {showRemove && (
            <button
              className="-mt-[0.125rem] align-top"
              onClick={() => handleRemove(params.row)}>
              <DeleteIcon color="buttonGrey" />
            </button>
          )}
        </Fragment>
      )
    });

  useEffect(() => {
    const rpmSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setRpmData(r);
      setLoading(false);
    });
    const rpmLoadingSub = loadingSubject().subscribe((l) => setTableLoading(l));

    return () => {
      if (rpmSub) rpmSub.unsubscribe();
      if (rpmLoadingSub) rpmLoadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchFunction(id, drawer)();
  }, [id, drawer]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit'
  };

  return (
    <div
      className="w-full"
      data-testid="rpm-mini-table-container">
      {loading && <ComponentSpinner label="Loading RPM Data" />}
      {!loading && (
        <UpstackDataGrid
          rows={rpmData.data || []}
          sxStyles={sxStyle}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'accountNumber', sort: 'desc' }] }
          }}
          currentPage={rpmData.page}
          rowCount={rpmData.totalCount || 0}
          columns={tableCols}
          dataCy="rpm-mini-data-grid-pagination"
          fetchFunction={fetchFunction(id, drawer)}
          title="rpmAccount"
          showSearch={false}
          page="RPMGrid_MiniTable"
          apiFilter
          gridSettings={{ muiConfig: {}, disabledFields: [] }}
          pageSizeOptions={[50]}
          showToolbar={false}
          withoutSave
          localLoading={tableLoading}
          pagination={rpmData.totalPages > 1}
          rowPrimaryKey="accountId"
        />
      )}
    </div>
  );
};
